import { useEffect, useState, useMemo } from 'react';
import { Event } from 'nostr-tools';
import { useAdmin } from '../contexts/AdminContext';
import { getNostrPool } from '../lib/nostrPool';
import { RELAYS } from '../config/constants';
import { BOT_CONFIG } from '../config/botConfig';

import { useGlobalModeration } from './useGlobalModeration';

export const useNostrEvents = (topic: string) => {
    const [events, setEvents] = useState<Event[]>([]);
    const [reactions, setReactions] = useState<Record<string, Record<string, { content: string, id: string, created_at: number }>>>({});  // targetId -> { pubkey -> { content, id, created_at } }
    const [isLoading, setIsLoading] = useState(true);
    const pool = getNostrPool(); // Use singleton
    const { settings } = useAdmin();
    const { blockedEventIds, blockedPubkeys } = useGlobalModeration(); // Global Moderation

    // Memoize customRelays array to prevent unnecessary re-renders
    const customRelaysStr = useMemo(() => settings.customRelays.join(','), [settings.customRelays]);
    const currentRelays = useMemo(() => {
        return [...new Set([...RELAYS, ...settings.customRelays])];
    }, [customRelaysStr]);


    useEffect(() => {
        if (!topic) return;

        setIsLoading(true);
        setEvents([]);
        setReactions({});

        const filter: any = {
            kinds: [1, 5, 7],
            '#t': [topic, topic.toLowerCase().replace(/\s+/g, '-')],
        };

        if (settings.isolateCommunity) {
            filter['#t'].push('mavililer');
        }

        const sub = pool.subscribeMany(currentRelays, filter, {
            onevent(event: Event) {
                if (event.kind === 1) {
                    // Collect raw events only (Filtering moves to useMemo)
                    setEvents((prev) => {
                        const exists = prev.some((e) => e.id === event.id);
                        if (exists) return prev;
                        return [...prev, event].sort((a, b) => b.created_at - a.created_at);
                    });
                } else if (event.kind === 7) {
                    // Reaction logic: Deduplicate by timestamp
                    const targetId = event.tags.find(t => t[0] === 'e')?.[1];
                    if (!targetId) return;

                    setReactions((prev) => {
                        const currentTargetVotes = prev[targetId] || {}; // pubkey -> { content, id, created_at }
                        const existingVote = currentTargetVotes[event.pubkey];

                        // Only update if no vote exists OR this vote is newer
                        if (!existingVote || event.created_at > existingVote.created_at) {
                            return {
                                ...prev,
                                [targetId]: {
                                    ...currentTargetVotes,
                                    [event.pubkey]: {
                                        content: event.content,
                                        id: event.id,
                                        created_at: event.created_at
                                    }
                                }
                            };
                        }
                        return prev;
                    });
                } else if (event.kind === 5) {
                    // Deletion logic...
                    // (Rest of kind 5 handling remains same, skipping brevity if not modifying)
                    const deletedIds = event.tags.filter(t => t[0] === 'e').map(t => t[1]);

                    setEvents((prev) => prev.filter(e => {
                        if (!deletedIds.includes(e.id)) return true
                        return e.pubkey !== event.pubkey
                    }));

                    setReactions((prev) => {
                        const next = { ...prev };
                        deletedIds.forEach(id => delete next[id]);
                        return next;
                    });
                }
            },
            oneose() {
                setIsLoading(false);
            },
        });

        // ... existing optimistic deletion logic ...
        const handleOptimisticDeletion = (e: CustomEvent) => {
            const { entryId } = e.detail
            setEvents((prev) => prev.filter(entry => entry.id !== entryId))
            setReactions((prev) => {
                const next = { ...prev }
                delete next[entryId]
                return next
            })
        }

        window.addEventListener('entry-deleted', handleOptimisticDeletion as EventListener)

        return () => {
            sub.close();
            window.removeEventListener('entry-deleted', handleOptimisticDeletion as EventListener)
        };
    }, [topic, currentRelays, settings.isolateCommunity, customRelaysStr, settings.deletedEventIds]);

    // Optimize array mapping with Object.assign AND perform Reactive Filtering
    const eventsWithVotes = useMemo(() => {
        return events
            .filter(event => {
                // ... filtering logic (unchanged) ...
                // 1. Global Admin Moderation Filter (REACTIVE)
                if (blockedPubkeys.has(event.pubkey)) return false;
                if (blockedEventIds.has(event.id)) return false;

                // 2. Local Mute Filter
                if (settings.mutedUsers.includes(event.pubkey)) return false;

                // 3. Bot Filter
                if (event.pubkey === BOT_CONFIG.pubkeyHex) return false;

                // 4. Banned Words Filter
                const isBanned = settings.bannedWords.some(word =>
                    event.content.toLowerCase().includes(word.toLowerCase())
                );
                if (isBanned) return false;

                // 5. Isolate Community Filter
                if (settings.isolateCommunity) {
                    const tags = event.tags.filter(t => t[0] === 't').map(t => t[1].toLowerCase());
                    const hyphenatedTopic = topic.toLowerCase().replace(/\s+/g, '-');
                    const hasTopic = tags.includes(topic.toLowerCase()) || tags.includes(hyphenatedTopic);
                    const hasMavililer = tags.includes('mavililer');
                    if (!hasTopic || !hasMavililer) return false;
                }

                return true;
            })
            .map(e => {
                const votes = reactions[e.id] || {};
                const voteValues = Object.values(votes);

                // Matching logic with useReactions.ts
                // Upvotes: "+", "👍", "❤️", "🤙", or empty/undefined
                const upvotes = voteValues.filter(v => {
                    const c = v.content;
                    return c === '+' || c === '👍' || c === '❤️' || c === '🤙' || !c || c === '';
                }).length;

                // Downvotes: "-", "👎"
                const downvotes = voteValues.filter(v => {
                    const c = v.content;
                    return c === '-' || c === '👎';
                }).length;

                // Normalize userVotes for EntryCard
                const normalizedUserVotes: Record<string, { content: string, id: string }> = {};
                Object.entries(votes).forEach(([pk, v]) => {
                    const isDown = v.content === '-' || v.content === '👎';
                    normalizedUserVotes[pk] = {
                        content: isDown ? '-' : '+',
                        id: v.id
                    };
                });

                return Object.assign({}, e, {
                    upvotes,
                    downvotes,
                    userVotes: normalizedUserVotes
                });
            });
    }, [events, reactions, blockedEventIds, blockedPubkeys, settings.mutedUsers, settings.bannedWords, settings.isolateCommunity, topic]);

    return { events: eventsWithVotes, isLoading };
};
